import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { login, identifyTenant, type TenantMatch } from '../../services/auth';
import MFAEnroll from './MFAEnroll';
import MFAVerify from './MFAVerify';

interface Props {
  onSuccess: () => void;
}

// Mismo criterio que el backend (SHARED_HOSTS, apps/core/src/plugins/tenant-host.ts):
// solo en el dominio compartido se pide el email antes que la contraseña.
const SHARED_HOSTS = (import.meta.env.VITE_SHARED_HOSTS ?? 'app.dimasfarming.com')
  .split(',').map((h: string) => h.trim().toLowerCase()).filter(Boolean);
const isSharedHost = SHARED_HOSTS.includes(window.location.hostname.toLowerCase());

export default function LoginForm({ onSuccess }: Props) {
  const { t } = useTranslation();
  const [step, setStep] = useState<'email' | 'tenant' | 'password'>(
    isSharedHost ? 'email' : 'password',
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tenantId, setTenantId] = useState<string | undefined>(undefined);
  const [tenantMatches, setTenantMatches] = useState<TenantMatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [enrollId, setEnrollId] = useState<string | null>(null);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [qrData, setQrData] = useState<{ qrDataUrl: string; secret: string } | null>(null);

  const handleIdentify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { matches } = await identifyTenant(email);
      if (matches.length === 0) {
        setError(t('auth.accountNotFound'));
      } else if (matches.length === 1) {
        setTenantId(matches[0].tenantId);
        setStep('password');
      } else {
        setTenantMatches(matches);
        setStep('tenant');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const chooseTenant = (match: TenantMatch) => {
    setTenantId(match.tenantId);
    setStep('password');
  };

  const goBackToEmail = () => {
    setPassword('');
    setError('');
    setStep('email');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await login(email, password, tenantId);

      if (response.success) {
        onSuccess();
      } else if (response.requiresMfaSetup) {
        const { enrollId: newEnrollId, qrDataUrl, manualKey } = response.requiresMfaSetup;
        setEnrollId(newEnrollId);
        setQrData({ qrDataUrl, secret: manualKey });
      } else if (response.requiresMfaVerify) {
        setChallengeId(response.requiresMfaVerify.challengeId);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (enrollId) {
    return <MFAEnroll enrollId={enrollId} qrData={qrData} onSuccess={onSuccess} />;
  }

  if (challengeId) {
    return <MFAVerify challengeId={challengeId} onSuccess={onSuccess} />;
  }

  return (
    <div className="auth-wrapper">
      <div className="auth-left">
        <div className="auth-brand">
          <div className="auth-logo">DIMAS FARMING</div>
          <div className="auth-tagline">{t('sidebar.platform')}</div>
        </div>
      </div>

      <div className="auth-right">
        {step === 'email' && (
          <form className="auth-form" onSubmit={handleIdentify}>
            <h1 className="auth-title">{t('auth.welcomeBack')}</h1>
            <p className="auth-subtitle">{t('auth.signInSubtitle')}</p>

            {error && <div className="error-message">{error}</div>}

            <div className="field">
              <label htmlFor="email">{t('auth.email')}</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
              />
            </div>

            <button type="submit" className="primary-button" disabled={loading}>
              {loading ? t('auth.signingIn') : t('auth.continue')}
            </button>
          </form>
        )}

        {step === 'tenant' && (
          <div className="auth-form">
            <h1 className="auth-title">{t('auth.chooseOrganization')}</h1>

            {error && <div className="error-message">{error}</div>}

            <div className="field">
              {tenantMatches.map((match) => (
                <button
                  key={match.tenantId}
                  type="button"
                  className="primary-button tenant-option"
                  onClick={() => chooseTenant(match)}
                >
                  {match.tenantName}
                </button>
              ))}
            </div>

            <button type="button" className="secondary-button" onClick={goBackToEmail}>
              {t('auth.back')}
            </button>
          </div>
        )}

        {step === 'password' && (
          <form className="auth-form" onSubmit={handleSubmit}>
            <h1 className="auth-title">{t('auth.welcomeBack')}</h1>
            <p className="auth-subtitle">{t('auth.signInSubtitle')}</p>

            {error && <div className="error-message">{error}</div>}

            <div className="field">
              <label htmlFor="email">{t('auth.email')}</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                disabled={isSharedHost}
              />
            </div>

            <div className="field">
              <label htmlFor="password">{t('auth.password')}</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                autoFocus={isSharedHost}
              />
            </div>

            <button type="submit" className="primary-button" disabled={loading}>
              {loading ? t('auth.signingIn') : t('auth.signIn')}
            </button>

            {isSharedHost && (
              <button type="button" className="secondary-button" onClick={goBackToEmail}>
                {t('auth.back')}
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
